import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase.js';
import { doc, setDoc } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import Swal from 'sweetalert2';
import { User, Mail, Shield } from 'lucide-react';
import '../../styles/Perfil.css';

const Perfil = ({ user, profileData, agreements }) => {
    // Estado para la información del perfil
    const [fullName, setFullName] = useState('');
    const [walletAddress, setWalletAddress] = useState('');
    const [walletNetwork, setWalletNetwork] = useState('USDT (TRC20)');
    const [loadingProfile, setLoadingProfile] = useState(false);

    // Estado para el cambio de contraseña
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loadingPassword, setLoadingPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const joinDate = useMemo(() => {
        if (!profileData?.createdAt) return null;
        const date = profileData.createdAt?.toDate ? profileData.createdAt.toDate() : new Date(profileData.createdAt);
        return isNaN(date) ? null : date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    }, [profileData]);

    const primaryInitial = useMemo(() => {
        if (profileData?.fullName) return profileData.fullName.charAt(0).toUpperCase();
        if (user?.displayName) return user.displayName.charAt(0).toUpperCase();
        if (user?.email) return user.email.charAt(0).toUpperCase();
        return 'U';
    }, [profileData, user]);

    useEffect(() => {
        if (profileData) {
            setFullName(profileData.fullName || '');
            setWalletAddress(profileData.walletAddress || '');
            setWalletNetwork(profileData.walletNetwork || 'USDT (TRC20)');
        }
    }, [profileData]);

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setLoadingProfile(true);
        const userProfileRef = doc(db, `users/${user.uid}/profile/data`);
        try {
            const updates = { 
                fullName, 
                walletAddress, 
                walletNetwork
            };
            await setDoc(userProfileRef, updates, { merge: true });
            Swal.fire('¡Éxito!', 'Tu perfil ha sido actualizado.', 'success');
        } catch (err) {
            Swal.fire('Error', 'No se pudo actualizar tu perfil.', 'error');
        } finally {
            setLoadingProfile(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (newPassword !== confirmPassword) {
            setError('Las nuevas contraseñas no coinciden.');
            return;
        }
        if (newPassword.length < 6) {
            setError('La nueva contraseña debe tener al menos 6 caracteres.');
            return;
        }

        setLoadingPassword(true);
        try {
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);
            
            setSuccess('¡Contraseña cambiada con éxito!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            
            Swal.fire('¡Éxito!', 'Tu contraseña ha sido cambiada.', 'success');

        } catch (err) {
            setError('La contraseña actual es incorrecta o ha ocurrido un error.');
            console.error(err);
        } finally {
            setLoadingPassword(false);
        }
    };

    return (
        <div className="main-panel">
            <h2 className="section-title">Mi Perfil</h2>
            <p>Gestiona tu información personal y la seguridad de tu cuenta.</p>

            <div className="profile-hero">
                <div className="profile-identity">
                    <div className="profile-avatar">{primaryInitial}</div>
                    <div>
                        <h3>{profileData?.fullName || user?.displayName || 'Usuario sin nombre'}</h3>
                                                {profileData?.isFoundingPartner && (
                                                    <span style={{
                                                        background: '#f59e0b',
                                                        color: '#fff',
                                                        borderRadius: '12px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 700,
                                                        padding: '2px 10px',
                                                        marginLeft: '8px',
                                                        letterSpacing: '0.04em',
                                                        textTransform: 'uppercase',
                                                        boxShadow: '0 1px 4px rgba(245,158,11,0.10)'
                                                    }}>
                                                        SOCIO FUNDADOR
                                                    </span>
                                                )}
                                                <p className="profile-email"><Mail size={16} /> {user?.email}</p>
                        <div className="profile-meta">
                            {joinDate && <span>Miembro desde {joinDate}</span>}
                            {profileData?.referralCode && <span>Código: {profileData.referralCode.toUpperCase()}</span>}
                        </div>
                    </div>
                </div>
            </div>

            <div className="profile-container">
                <div className="profile-card">
                    <div className="card-heading">
                        <div className="icon-pill neutral"><User size={18} /></div>
                        <div>
                            <h3 className="section-subtitle">Información Personal y de Retiro</h3>
                            <p className="card-copy">Actualiza tus datos visibles y la billetera donde recibirás tus retiros.</p>
                        </div>
                    </div>
                    <form onSubmit={handleProfileSubmit}>
                        <div className="input-group">
                            <label htmlFor="fullName">Nombre Completo</label>
                            <input id="fullName" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} />
                        </div>
                        <div className="profile-form-grid">
                            <div className="input-group">
                                <label htmlFor="walletAddress">Dirección de Billetera USDT</label>
                                <input id="walletAddress" type="text" value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} placeholder="Pega tu dirección de billetera" />
                            </div>
                            <div className="input-group">
                                <label htmlFor="walletNetwork">Red de la Billetera</label>
                                <select id="walletNetwork" value={walletNetwork} onChange={(e) => setWalletNetwork(e.target.value)}>
                                    <option value="USDT (TRC20)">USDT (TRC20)</option>
                                    <option value="USDT (BEP20)">USDT (BEP20)</option>
                                    <option value="USDT (ERC20)">USDT (ERC20)</option>
                                </select>
                            </div>
                        </div>
                        <button type="submit" className="login-button" disabled={loadingProfile}>
                            {loadingProfile ? 'Guardando...' : 'Guardar Cambios'}
                        </button>
                    </form>
                </div>
                
                <div className="profile-card">
                    <div className="card-heading">
                        <div className="icon-pill warning"><Shield size={18} /></div>
                        <div>
                            <h3 className="section-subtitle">Cambiar Contraseña</h3>
                            <p className="card-copy">Reforzamos la seguridad solicitando tu contraseña actual antes de guardar la nueva.</p>
                        </div>
                    </div>
                    <form onSubmit={handlePasswordSubmit}>
                        <div className="profile-form-grid password-grid">
                            <div className="input-group"><label htmlFor="currentPassword">Contraseña Actual</label><input id="currentPassword" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required /></div>
                            <div className="input-group"><label htmlFor="newPassword">Nueva Contraseña</label><input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required /></div>
                            <div className="input-group"><label htmlFor="confirmPassword">Confirmar Nueva Contraseña</label><input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required /></div>
                        </div>
                        {error && <p className="message-box error">{error}</p>}
                        {success && <p className="message-box success">{success}</p>}
                        <button type="submit" className="login-button" disabled={loadingPassword}>
                            {loadingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Perfil;